import { useMemo, useRef, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { FileX2, SearchX } from "lucide-react";
import { FeedbackState, type DataTableColumn } from "@h2j-data-team/ui";
import type { QvdFile, QvdFilesSort } from "@repo/shared";

import { formatBytes } from "@/lib/health-format";
import { qvdFilesInfiniteQueryOptions } from "@/features/qvd-analysis/api";
import { VirtualRowList } from "@/components/shared/VirtualRowList";
import {
  ListError,
  ListLoading,
  QvdListToolbar,
  QvdPathLabel,
  RowButton,
  SpaceLink,
} from "./qvd-list-parts";
import { QvdFileConsumingAppsDrawer } from "./QvdConsumingAppsDrawer";
import { SERVER_SORT_DIRECTION, useDebouncedValue } from "./qvd-list-utils";

// QvdFilesTable lists the run's FULL file inventory (every type, not only QVDs).
// Search, space filter, and sort run server-side over the whole set; rows are
// pulled a page at a time on scroll and virtualized — a run can hold hundreds of
// thousands of files.
export function QvdFilesTable({
  runId,
  spaceOptions,
  totalCount,
}: {
  runId: string;
  spaceOptions: string[];
  totalCount: number;
}) {
  const { t } = useTranslation("qvd-analysis");
  const [search, setSearch] = useState("");
  const [spaceFilter, setSpaceFilter] = useState<string[]>([]);
  const [sortKey, setSortKey] = useState<QvdFilesSort>("size");
  const debouncedSearch = useDebouncedValue(search, 300);
  // Kept after closing so the drawer keeps its content while it animates out;
  // the button that opened it gets the focus back.
  const [detailFile, setDetailFile] = useState<QvdFile | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const opener = useRef<HTMLElement | null>(null);

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(
    qvdFilesInfiniteQueryOptions(runId, {
      search: debouncedSearch,
      space: spaceFilter[0] ?? "",
      sort: sortKey,
    }),
  );

  const rows = useMemo(() => data?.pages.flatMap((p) => p.data) ?? [], [data]);
  const filtering = !!debouncedSearch || spaceFilter.length > 0;

  // Only a file with counted consumers has a detail to open. The other two states
  // are NOT the same thing and must not look alike: 0 means the scan found nobody
  // reading the file, null means it produced no consumer evidence at all (a
  // non-QVD, a run stopped before the orphan phase, a run older than the column)
  // — which is never a claim that nobody reads it.
  const hasConsumers = (f: QvdFile) => (f.consumerCount ?? 0) > 0;

  const columns = useMemo<DataTableColumn<QvdFile>[]>(
    () => [
      {
        id: "name",
        header: t("footprint.cols.file"),
        sort: "text",
        rowLink: true,
        cell: (f) =>
          hasConsumers(f) ? (
            <RowButton
              label={t("columns.consumers.openLabel", { name: f.name })}
              onClick={(button) => {
                opener.current = button;
                setDetailFile(f);
                setDetailOpen(true);
              }}
            >
              <QvdPathLabel path={f.path} />
            </RowButton>
          ) : (
            <QvdPathLabel path={f.path} />
          ),
      },
      {
        id: "type",
        header: t("footprint.cols.fileType"),
        width: 100,
        cell: (f) => <span className="text-dim">{f.ext ? f.ext.toUpperCase() : "—"}</span>,
      },
      {
        id: "space",
        header: t("footprint.cols.space"),
        width: 200,
        cell: (f) => (
          <span className="text-dim">
            <SpaceLink spaceName={f.spaceName} />
          </span>
        ),
      },
      {
        id: "consumers",
        header: t("files.cols.consumers"),
        width: 90,
        align: "end",
        variant: "numeric",
        cell: (f) =>
          f.consumerCount == null ? (
            <span className="text-faint" title={t("files.consumers.unknown")}>
              —
            </span>
          ) : f.consumerCount === 0 ? (
            <span className="font-sans text-status-warning" title={t("files.consumers.none")}>
              {t("files.consumers.zero")}
            </span>
          ) : (
            f.consumerCount
          ),
      },
      {
        id: "size",
        header: t("footprint.cols.size"),
        width: 120,
        align: "end",
        variant: "numeric",
        sort: "number",
        cell: (f) => formatBytes(f.sizeBytes),
      },
    ],
    [t],
  );

  return (
    <div className="flex flex-col">
      {/* Outside the loading branch: a filter change starts a fresh query, and
          unmounting the toolbar under it would drop the focused search box. */}
      <QvdListToolbar
        search={search}
        onSearch={setSearch}
        searchPlaceholder={t("files.searchPlaceholder")}
        spaceOptions={spaceOptions}
        spaceFilter={spaceFilter}
        onSpaceFilter={setSpaceFilter}
        singleSpace
      />

      <p className="pb-2 text-sm text-dim">
        {t("files.summary", { total: totalCount.toLocaleString() })}
      </p>

      {isLoading ? (
        <ListLoading />
      ) : isError ? (
        <ListError error={error} title={t("files.error")} onRetry={() => void refetch()} />
      ) : rows.length === 0 ? (
        <FeedbackState
          state={filtering ? "no-results" : "empty"}
          icon={filtering ? <SearchX /> : <FileX2 />}
          title={filtering ? t("files.empty.filteredTitle") : t("files.empty.title")}
          description={
            filtering ? t("files.empty.filteredDescription") : t("files.empty.description")
          }
        />
      ) : (
        <VirtualRowList
          label={t("tabs.files")}
          columns={columns}
          rows={rows}
          getKey={(f) => f.id}
          linked={hasConsumers}
          sort={{ id: sortKey, direction: SERVER_SORT_DIRECTION[sortKey] }}
          onSort={(column) =>
            setSortKey(column.id === sortKey ? "size" : (column.id as QvdFilesSort))
          }
          onReachEnd={() => {
            if (hasNextPage && !isFetchingNextPage) fetchNextPage();
          }}
          loadingMoreLabel={isFetchingNextPage ? t("loadingMore") : undefined}
        />
      )}

      <QvdFileConsumingAppsDrawer
        open={detailOpen}
        onOpenChange={setDetailOpen}
        runId={runId}
        file={detailFile}
        restoreFocusRef={opener}
      />
    </div>
  );
}
